import { useState } from 'react'
import { Card, Button, Input, Space, Typography, Tag, Tooltip, Divider, message } from 'antd'
import {
  LinkOutlined,
  CopyOutlined,
  SaveOutlined,
  CheckOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
} from '@ant-design/icons'

const { Text, Paragraph } = Typography

export interface CampoIntegrazione {
  etichetta: string
  chiave: string
  tipo: 'string' | 'secret' | 'url' | 'boolean'
  valore: string | null
  placeholder?: string
  descrizione?: string
  copiabile?: boolean    // mostra bottone copia (default true per non-secret)
  readonly?: boolean     // campo non editabile (es. URL fisso)
}

interface Props {
  titolo: string
  descrizione?: string
  url?: string           // link al servizio esterno (docs / dashboard)
  urlLabel?: string      // etichetta del bottone link (default: "Apri sito")
  campi: CampoIntegrazione[]
  saving?: Record<string, boolean>
  onSave?: (chiave: string, valore: string | null) => Promise<void>
}

export const PannelloIntegrazioneEsterna = ({
  titolo,
  descrizione,
  url,
  urlLabel = 'Apri sito',
  campi,
  saving = {},
  onSave,
}: Props) => {
  const [drafts, setDrafts] = useState<Record<string, string>>({})

  const getDraft = (campo: CampoIntegrazione) =>
    drafts[campo.chiave] !== undefined ? drafts[campo.chiave] : (campo.valore ?? '')

  const handleCopy = (val: string) => {
    navigator.clipboard.writeText(val).then(
      () => message.success('Copiato negli appunti'),
      () => message.error('Impossibile copiare'),
    )
  }

  const handleSave = async (campo: CampoIntegrazione) => {
    if (!onSave) return
    const val = drafts[campo.chiave] !== undefined ? drafts[campo.chiave] : (campo.valore ?? '')
    await onSave(campo.chiave, val || null)
    setDrafts((prev) => {
      const next = { ...prev }
      delete next[campo.chiave]
      return next
    })
  }

  const isDirty = (campo: CampoIntegrazione) =>
    drafts[campo.chiave] !== undefined && drafts[campo.chiave] !== (campo.valore ?? '')

  return (
    <Card
      size="small"
      title={
        <Space>
          <Text strong>{titolo}</Text>
        </Space>
      }
      extra={
        url ? (
          <Button
            size="small"
            type="link"
            icon={<LinkOutlined />}
            onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
          >
            {urlLabel}
          </Button>
        ) : undefined
      }
      style={{ marginBottom: 16 }}
    >
      {descrizione && (
        <Paragraph type="secondary" style={{ marginBottom: 16, fontSize: 13 }}>
          {descrizione}
        </Paragraph>
      )}

      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        {campi.map((campo, idx) => {
          const draft = getDraft(campo)
          const dirty = isDirty(campo)
          const isSaving = saving[campo.chiave]
          const canCopy =
            campo.copiabile !== false && campo.tipo !== 'secret' && !!draft

          return (
            <div key={campo.chiave}>
              {idx > 0 && <Divider style={{ margin: '4px 0 12px' }} />}
              <Space direction="vertical" style={{ width: '100%' }} size={4}>
                <Space size={6}>
                  <Text style={{ fontSize: 13 }}>{campo.etichetta}</Text>
                  {campo.tipo === 'secret' && (
                    <Tag color="orange" style={{ fontSize: 11 }}>segreto</Tag>
                  )}
                  {campo.tipo === 'url' && (
                    <Tag color="blue" style={{ fontSize: 11 }}>URL</Tag>
                  )}
                </Space>
                {campo.descrizione && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {campo.descrizione}
                  </Text>
                )}
                <Space.Compact style={{ width: '100%' }}>
                  {campo.tipo === 'secret' ? (
                    <Input.Password
                      value={draft}
                      placeholder={campo.placeholder ?? 'Inserisci valore…'}
                      onChange={(e) =>
                        setDrafts((prev) => ({ ...prev, [campo.chiave]: e.target.value }))
                      }
                      disabled={campo.readonly || isSaving}
                      iconRender={(visible) =>
                        visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                      }
                      style={{ flex: 1 }}
                    />
                  ) : (
                    <Input
                      value={draft}
                      placeholder={campo.placeholder ?? 'Inserisci valore…'}
                      onChange={(e) =>
                        setDrafts((prev) => ({ ...prev, [campo.chiave]: e.target.value }))
                      }
                      disabled={campo.readonly || isSaving}
                      style={{ flex: 1 }}
                    />
                  )}

                  {canCopy && (
                    <Tooltip title="Copia">
                      <Button icon={<CopyOutlined />} onClick={() => handleCopy(draft)} />
                    </Tooltip>
                  )}

                  {!campo.readonly && onSave && (
                    <Tooltip title={dirty ? 'Salva' : 'Già aggiornato'}>
                      <Button
                        type={dirty ? 'primary' : 'default'}
                        icon={dirty ? <SaveOutlined /> : <CheckOutlined />}
                        loading={isSaving}
                        onClick={() => handleSave(campo)}
                      />
                    </Tooltip>
                  )}

                  {campo.tipo === 'url' && draft && (
                    <Tooltip title="Apri URL">
                      <Button
                        icon={<LinkOutlined />}
                        onClick={() => window.open(draft, '_blank', 'noopener,noreferrer')}
                      />
                    </Tooltip>
                  )}
                </Space.Compact>
              </Space>
            </div>
          )
        })}
      </Space>
    </Card>
  )
}

export default PannelloIntegrazioneEsterna
